'use client';

import { useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import {
  compileModifierShader,
  updateModifierUniforms,
  type GpuModifierDef,
  type ModifierUniforms,
  type CompiledShaderResult,
} from './modifier-shaders';
import { applyCpuModifiers, ensureGeometryTangents, type CpuModifierDef } from './modifier-cpu';
import {
  combineUvRandomizeSeed,
  createSeededRng,
  extractUvRandomizeMetaFromMaterial,
  randomInRangeWithStep,
  uvRandomizeMetaHasVariance,
  type C3dUvRandomizeMeta,
} from './material-graph-eval';
import {
  transmissionFrontCullInstancedAfterRender,
  transmissionFrontCullInstancedBeforeRender,
} from './transmission-front-cull';

export interface ModifierDef {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  name?: string;
  tags?: string[];
  params: Record<string, number | boolean | string>;
}

export interface ModifiedMeshInstancePointerHandlers {
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
  onPointerOver?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerOut?: (e: ThreeEvent<PointerEvent>) => void;
}

interface ModifiedMeshProps {
  children: React.ReactNode;
  modifiers?: ModifierDef[];
  visible?: boolean;
  stayOnTop?: boolean;
  getCenterPosition?: () => { x: number; y: number; z: number } | null;
  /** Forward picking/hover from InstancedMesh draws (source meshes are hidden while array is active). */
  instancePointerHandlers?: ModifiedMeshInstancePointerHandlers;
}

const GPU_TYPES = new Set(['bend', 'twist', 'taper', 'noise']);
const CPU_TYPES = new Set(['slice', 'smooth', 'normal', 'optimize', 'shell', 'uvPlanar', 'uvBox', 'uvCylindrical', 'uvOrganic', 'uvTransformMod']);
const ARRAY_TYPES = new Set(['arrayLinear', 'arrayRadial']);

const DEG2RAD = Math.PI / 180;

function computeArrayMatrices(mod: ModifierDef): THREE.Matrix4[] {
  const count = Math.max(1, Math.floor(mod.params.count as number));
  if (count <= 1) return [new THREE.Matrix4()];

  const px = (mod.params.pivotX as number) || 0;
  const py = (mod.params.pivotY as number) || 0;
  const pz = (mod.params.pivotZ as number) || 0;
  const hasPivot = px !== 0 || py !== 0 || pz !== 0;

  const toPivot = hasPivot ? new THREE.Matrix4().makeTranslation(-px, -py, -pz) : null;
  const fromPivot = hasPivot ? new THREE.Matrix4().makeTranslation(px, py, pz) : null;

  const matrices: THREE.Matrix4[] = [];

  if (mod.type === 'arrayLinear') {
    const ox = (mod.params.offsetX as number) || 0;
    const oy = (mod.params.offsetY as number) || 0;
    const oz = (mod.params.offsetZ as number) || 0;
    const rx = ((mod.params.rotX as number) || 0) * DEG2RAD;
    const ry = ((mod.params.rotY as number) || 0) * DEG2RAD;
    const rz = ((mod.params.rotZ as number) || 0) * DEG2RAD;
    const sx = (mod.params.scaleX as number) || 0;
    const sy = (mod.params.scaleY as number) || 0;
    const sz = (mod.params.scaleZ as number) || 0;

    const euler = new THREE.Euler();
    const quat = new THREE.Quaternion();

    for (let i = 0; i < count; i++) {
      const m = new THREE.Matrix4();
      const pos = new THREE.Vector3(ox * i, oy * i, oz * i);
      euler.set(rx * i, ry * i, rz * i);
      quat.setFromEuler(euler);
      const s = new THREE.Vector3(1 + sx * i, 1 + sy * i, 1 + sz * i);
      m.compose(pos, quat, s);

      if (hasPivot) {
        const pivoted = new THREE.Matrix4();
        pivoted.multiplyMatrices(fromPivot!, m);
        pivoted.multiply(toPivot!);
        matrices.push(pivoted);
      } else {
        matrices.push(m);
      }
    }
  } else if (mod.type === 'arrayRadial') {
    const axis = (mod.params.axis as string) || 'y';
    const radius = (mod.params.radius as number) || 1;
    const totalAngle = ((mod.params.totalAngle as number) || 360) * DEG2RAD;
    const align = mod.params.alignToCircle as boolean;
    const heightOffset = (mod.params.offsetY as number) || 0;

    for (let i = 0; i < count; i++) {
      const angle = (totalAngle / count) * i;
      const m = new THREE.Matrix4();
      const pos = new THREE.Vector3();
      const quat = new THREE.Quaternion();

      if (axis === 'y') {
        pos.set(Math.cos(angle) * radius, heightOffset * i, Math.sin(angle) * radius);
        if (align) quat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), -angle + Math.PI / 2);
      } else if (axis === 'x') {
        pos.set(heightOffset * i, Math.cos(angle) * radius, Math.sin(angle) * radius);
        if (align) quat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -angle + Math.PI / 2);
      } else {
        pos.set(Math.cos(angle) * radius, Math.sin(angle) * radius, heightOffset * i);
        if (align) quat.setFromAxisAngle(new THREE.Vector3(0, 0, 1), -angle + Math.PI / 2);
      }

      m.compose(pos, quat, new THREE.Vector3(1, 1, 1));

      if (hasPivot) {
        const pivoted = new THREE.Matrix4();
        pivoted.multiplyMatrices(fromPivot!, m);
        pivoted.multiply(toPivot!);
        matrices.push(pivoted);
      } else {
        matrices.push(m);
      }
    }
  }

  return matrices;
}

const C3D_INST_UV_ATTR = 'c3dInstUv';
const C3D_INST_UV_SCL_ATTR = 'c3dInstUvScl';

function pnum(params: Record<string, number | boolean | string>, key: string, fallback: number): number {
  const v = params[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function instanceUvParamsHaveVariance(params: Record<string, number | boolean | string>): boolean {
  const minU = pnum(params, 'instanceUvMinU', 0);
  const maxU = pnum(params, 'instanceUvMaxU', 0);
  const minV = pnum(params, 'instanceUvMinV', 0);
  const maxV = pnum(params, 'instanceUvMaxV', 0);
  const rotMin = pnum(params, 'instanceUvRotMinDeg', 0);
  const rotMax = pnum(params, 'instanceUvRotMaxDeg', 0);
  const scaleMin = pnum(params, 'instanceUvScaleMin', 1);
  const scaleMax = pnum(params, 'instanceUvScaleMax', 1);
  return (
    minU !== maxU ||
    minV !== maxV ||
    rotMin !== rotMax ||
    scaleMin !== scaleMax ||
    !!params.instanceUvMirrorU ||
    !!params.instanceUvMirrorV
  );
}

function metaToInstanceUvParams(meta: C3dUvRandomizeMeta): Record<string, number | boolean | string> {
  return {
    instanceUvMinU: meta.offsetMinU,
    instanceUvMaxU: meta.offsetMaxU,
    instanceUvStepU: meta.offsetStepU,
    instanceUvMinV: meta.offsetMinV,
    instanceUvMaxV: meta.offsetMaxV,
    instanceUvStepV: meta.offsetStepV,
    instanceUvRotMinDeg: meta.rotationMinDeg,
    instanceUvRotMaxDeg: meta.rotationMaxDeg,
    instanceUvRotStepDeg: meta.rotationStepDeg,
    instanceUvScaleMin: meta.scaleMin,
    instanceUvScaleMax: meta.scaleMax,
    instanceUvScaleStep: meta.scaleStep,
    instanceUvMirrorU: meta.mirrorU,
    instanceUvMirrorV: meta.mirrorV,
  };
}

/**
 * Array "Randomize UV per copy" + UV Randomizer "Per mesh" both need per-instance attributes.
 * Prefer explicit array ranges when they have variance; otherwise fall back to graph ranges
 * stamped on the material maps (so identity array UV + Per mesh actually varies each copy).
 */
function resolveInstanceUvSetup(
  arrayParams: Record<string, number | boolean | string>,
  srcMat: THREE.Material | THREE.Material[],
): { enabled: boolean; params: Record<string, number | boolean | string>; seed: number } {
  const arrayOn = !!arrayParams.randomizeInstanceUv;
  const arrayVariance = instanceUvParamsHaveVariance(arrayParams);
  const meta = extractUvRandomizeMetaFromMaterial(srcMat);
  const metaOk = !!(meta && uvRandomizeMetaHasVariance(meta));
  const autoFromPerMesh = !!(meta?.perMesh && metaOk);

  if (!arrayOn && !autoFromPerMesh) {
    return { enabled: false, params: arrayParams, seed: 1 };
  }

  // Identity Array UV with no graph Per-mesh meta: share the live material (Metal Mesh,
  // wood before graph meta arrives). Do NOT blanket-disable autoFromPerMesh — Wood v3
  // forces randomizeInstanceUv=false / zero Array ranges and relies on graph Per-mesh
  // UV meta → one InstancedMesh clone + instance attrs (Cap keeps the shared live mat).
  if (!arrayVariance && !autoFromPerMesh) {
    return { enabled: false, params: arrayParams, seed: 1 };
  }

  let params = arrayParams;
  let seed = Math.floor(Number(arrayParams.instanceUvSeed) || 1);
  if ((!arrayVariance || !arrayOn) && metaOk && meta) {
    params = { ...arrayParams, ...metaToInstanceUvParams(meta) };
    seed = Math.floor(Number(meta.seed) || seed) || 1;
  }
  return { enabled: true, params, seed };
}

function fillInstanceUvAttributes(
  geometry: THREE.BufferGeometry,
  instanceCount: number,
  baseSeed: number,
  params: Record<string, number | boolean | string>,
) {
  const minU = pnum(params, 'instanceUvMinU', 0);
  const maxU = pnum(params, 'instanceUvMaxU', 1);
  const minV = pnum(params, 'instanceUvMinV', 0);
  const maxV = pnum(params, 'instanceUvMaxV', 1);
  const stepU = pnum(params, 'instanceUvStepU', 0);
  const stepV = pnum(params, 'instanceUvStepV', 0);
  const rotMin = pnum(params, 'instanceUvRotMinDeg', 0);
  const rotMax = pnum(params, 'instanceUvRotMaxDeg', 360);
  const rotStep = pnum(params, 'instanceUvRotStepDeg', 0);
  const scaleMin = pnum(params, 'instanceUvScaleMin', 0.82);
  const scaleMax = pnum(params, 'instanceUvScaleMax', 1.18);
  const scaleStep = pnum(params, 'instanceUvScaleStep', 0);
  const mirrorU = !!params.instanceUvMirrorU;
  const mirrorV = !!params.instanceUvMirrorV;
  const loU = Math.min(minU, maxU);
  const hiU = Math.max(minU, maxU);
  const loV = Math.min(minV, maxV);
  const hiV = Math.max(minV, maxV);
  const loR = Math.min(rotMin, rotMax);
  const hiR = Math.max(rotMin, rotMax);
  const loS = Math.min(scaleMin, scaleMax);
  const hiS = Math.max(scaleMin, scaleMax);

  const arr4 = new Float32Array(instanceCount * 4);
  const arr2 = new Float32Array(instanceCount * 2);
  for (let i = 0; i < instanceCount; i++) {
    const rng = createSeededRng(combineUvRandomizeSeed(baseSeed, `iuv:${i}`));
    const ru = randomInRangeWithStep(loU, hiU, stepU > 0 ? stepU : 0, rng);
    const rv = randomInRangeWithStep(loV, hiV, stepV > 0 ? stepV : 0, rng);
    const rotDeg = randomInRangeWithStep(loR, hiR, rotStep > 0 ? rotStep : 0, rng);
    const rot = (rotDeg * Math.PI) / 180;
    arr4[i * 4 + 0] = ru;
    arr4[i * 4 + 1] = rv;
    arr4[i * 4 + 2] = Math.cos(rot);
    arr4[i * 4 + 3] = Math.sin(rot);
    const s = randomInRangeWithStep(loS, hiS, scaleStep > 0 ? scaleStep : 0, rng);
    // Negative scale mirrors around UV center (matches texture.repeat flip).
    const su = mirrorU && rng() < 0.5 ? -s : s;
    const sv = mirrorV && rng() < 0.5 ? -s : s;
    arr2[i * 2 + 0] = su;
    arr2[i * 2 + 1] = sv;
  }
  geometry.setAttribute(C3D_INST_UV_ATTR, new THREE.InstancedBufferAttribute(arr4, 4));
  geometry.setAttribute(C3D_INST_UV_SCL_ATTR, new THREE.InstancedBufferAttribute(arr2, 2));
}

function applyInstancedUvToMaterial(mat: THREE.Material): THREE.Material {
  const cloned = mat.clone() as THREE.Material;
  const m = cloned as THREE.Material & { defines?: Record<string, unknown> };
  m.defines = { ...(m.defines ?? {}), C3D_INST_UV: 1 };
  const prev = cloned.onBeforeCompile;
  cloned.onBeforeCompile = (shader, renderer) => {
    if (prev) prev.bind(cloned)(shader, renderer);
    const vs = shader.vertexShader;
    if (!vs.includes('c3dInstUv')) {
      shader.vertexShader = `attribute vec4 c3dInstUv;\nattribute vec2 c3dInstUvScl;\n${vs}`;
    }
    // Three.js ≥~r160 sets vMapUv / vNormalMapUv / … separately from vUv; fragment shaders sample maps via those varyings.
    // Transform mesh `uv` (channel 0), then recompute every map UV that three builds from MAP_* macros (assumes UV set 0).
    shader.vertexShader = shader.vertexShader.replace(
      '#include <uv_vertex>',
      `#include <uv_vertex>
#if defined( C3D_INST_UV )
vec2 c3d_p = ( uv - 0.5 ) * c3dInstUvScl;
float c3d_cr = c3dInstUv.z, c3d_sr = c3dInstUv.w;
vec2 c3d_uv = vec2( c3d_cr * c3d_p.x - c3d_sr * c3d_p.y, c3d_sr * c3d_p.x + c3d_cr * c3d_p.y ) + 0.5 + c3dInstUv.xy;
#if defined( USE_UV ) || defined( USE_ANISOTROPY )
vUv = c3d_uv;
#endif
#ifdef USE_MAP
vMapUv = ( mapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_ALPHAMAP
vAlphaMapUv = ( alphaMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_LIGHTMAP
vLightMapUv = ( lightMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_AOMAP
vAoMapUv = ( aoMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_BUMPMAP
vBumpMapUv = ( bumpMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_NORMALMAP
vNormalMapUv = ( normalMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_DISPLACEMENTMAP
vDisplacementMapUv = ( displacementMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_EMISSIVEMAP
vEmissiveMapUv = ( emissiveMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_METALNESSMAP
vMetalnessMapUv = ( metalnessMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_ROUGHNESSMAP
vRoughnessMapUv = ( roughnessMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_ANISOTROPYMAP
vAnisotropyMapUv = ( anisotropyMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_CLEARCOATMAP
vClearcoatMapUv = ( clearcoatMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_CLEARCOAT_NORMALMAP
vClearcoatNormalMapUv = ( clearcoatNormalMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_CLEARCOAT_ROUGHNESSMAP
vClearcoatRoughnessMapUv = ( clearcoatRoughnessMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_IRIDESCENCEMAP
vIridescenceMapUv = ( iridescenceMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_IRIDESCENCE_THICKNESSMAP
vIridescenceThicknessMapUv = ( iridescenceThicknessMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_SHEEN_COLORMAP
vSheenColorMapUv = ( sheenColorMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_SHEEN_ROUGHNESSMAP
vSheenRoughnessMapUv = ( sheenRoughnessMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_SPECULARMAP
vSpecularMapUv = ( specularMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_SPECULAR_COLORMAP
vSpecularColorMapUv = ( specularColorMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_SPECULAR_INTENSITYMAP
vSpecularIntensityMapUv = ( specularIntensityMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_TRANSMISSIONMAP
vTransmissionMapUv = ( transmissionMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#ifdef USE_THICKNESSMAP
vThicknessMapUv = ( thicknessMapTransform * vec3( c3d_uv, 1 ) ).xy;
#endif
#endif
`,
    );
  };
  cloned.needsUpdate = true;
  return cloned;
}

function applyInstancedUvMaterial(mat: THREE.Material | THREE.Material[]): THREE.Material | THREE.Material[] {
  if (Array.isArray(mat)) return mat.map((m) => applyInstancedUvToMaterial(m));
  return applyInstancedUvToMaterial(mat);
}

/**
 * Array Radial with Per-mesh UV clones geometry once. GlbModel may compute tangents on the
 * *source* mesh later — without copying them, InstancedMesh renders without USE_TANGENT and
 * anisotropy looks isotropic while the material shelf sphere (own geometry) looks correct.
 */
function syncInstanceGeometryTangents(srcMesh: THREE.Mesh, instMesh: THREE.InstancedMesh): void {
  const srcGeom = srcMesh.geometry;
  const dstGeom = instMesh.geometry;
  if (!srcGeom || !dstGeom || srcGeom === dstGeom) return;

  if (srcGeom.hasAttribute('tangent')) {
    const srcTan = srcGeom.getAttribute('tangent');
    const dstTan = dstGeom.getAttribute('tangent');
    const ud = dstGeom.userData as { __c3dTanCopiedCount?: number };
    if (!dstTan || dstTan.count !== srcTan.count || ud.__c3dTanCopiedCount !== srcTan.count) {
      dstGeom.setAttribute('tangent', srcTan.clone());
      ud.__c3dTanCopiedCount = srcTan.count;
    }
    return;
  }

  if (!dstGeom.hasAttribute('tangent')) {
    ensureGeometryTangents(dstGeom);
  }
}

/** Tracks source material so InstancedMesh can re-clone when GlbModel updates the hidden source mesh. */
type InstUvTrackUserData = {
  __c3dIvSrcRef?: THREE.Material | THREE.Material[] | null;
  __c3dIvSrcVer?: number;
  __c3dIvSrcVers?: number[];
  __c3dIvSrcMap?: string;
  __c3dIvSrcMaps?: string[];
  /** Fingerprint of seed + ranges used to fill instance UV attrs (graph may arrive after first create). */
  __c3dIvParamsKey?: string;
};

function instanceUvParamsKey(
  seed: number,
  params: Record<string, number | boolean | string>,
): string {
  return [
    seed,
    pnum(params, 'instanceUvMinU', 0),
    pnum(params, 'instanceUvMaxU', 0),
    pnum(params, 'instanceUvStepU', 0),
    pnum(params, 'instanceUvMinV', 0),
    pnum(params, 'instanceUvMaxV', 0),
    pnum(params, 'instanceUvStepV', 0),
    pnum(params, 'instanceUvRotMinDeg', 0),
    pnum(params, 'instanceUvRotMaxDeg', 0),
    pnum(params, 'instanceUvRotStepDeg', 0),
    pnum(params, 'instanceUvScaleMin', 1),
    pnum(params, 'instanceUvScaleMax', 1),
    pnum(params, 'instanceUvScaleStep', 0),
    params.instanceUvMirrorU ? 1 : 0,
    params.instanceUvMirrorV ? 1 : 0,
  ].join('|');
}

function materialMapFingerprint(mat: THREE.Material): string {
  if (!(mat instanceof THREE.MeshStandardMaterial)) return mat.uuid;
  const slots = [
    'map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'alphaMap',
    'bumpMap', 'displacementMap', 'anisotropyMap',
  ] as const;
  const parts: string[] = [];
  for (const s of slots) {
    const t = (mat as any)[s] as THREE.Texture | null | undefined;
    if (!t) continue;
    const img = t.image as { width?: number; height?: number } | undefined;
    parts.push(`${s}:${t.uuid}:${img?.width ?? 0}x${img?.height ?? 0}`);
  }
  const share = (mat.userData as { __c3dShareKey?: string } | undefined)?.__c3dShareKey;
  if (share) parts.push(`sk:${share}`);
  return parts.join('|') || mat.uuid;
}

function instancedUvMaterialsOutOfSync(
  src: THREE.Material | THREE.Material[],
  u: InstUvTrackUserData,
): boolean {
  if (Array.isArray(src)) {
    const n = src.length;
    if (!u.__c3dIvSrcVers || u.__c3dIvSrcVers.length !== n) return true;
    if (u.__c3dIvSrcRef !== src) return true;
    if (!u.__c3dIvSrcMaps || u.__c3dIvSrcMaps.length !== n) return true;
    for (let i = 0; i < n; i++) {
      if ((u.__c3dIvSrcVers[i] ?? -1) !== src[i]!.version) return true;
      if ((u.__c3dIvSrcMaps[i] ?? '') !== materialMapFingerprint(src[i]!)) return true;
    }
    return false;
  }
  if (u.__c3dIvSrcRef !== src) return true;
  if ((u.__c3dIvSrcVer ?? -1) !== src.version) return true;
  if ((u.__c3dIvSrcMap ?? '') !== materialMapFingerprint(src)) return true;
  return false;
}

function rememberInstancedUvMaterials(src: THREE.Material | THREE.Material[], u: InstUvTrackUserData) {
  u.__c3dIvSrcRef = src;
  if (Array.isArray(src)) {
    u.__c3dIvSrcVers = src.map((m) => m.version);
    u.__c3dIvSrcMaps = src.map((m) => materialMapFingerprint(m));
    u.__c3dIvSrcVer = undefined;
    u.__c3dIvSrcMap = undefined;
  } else {
    u.__c3dIvSrcVer = src.version;
    u.__c3dIvSrcMap = materialMapFingerprint(src);
    u.__c3dIvSrcVers = undefined;
    u.__c3dIvSrcMaps = undefined;
  }
}

/**
 * Instance-UV clones are frozen snapshots — Cap (live mat) can finish a tint lerp while
 * Body clones stay on the old color until `mat.version` bumps. Copy live scalars every
 * frame so carpaint Cap + array Body stay locked.
 *
 * Do not copy texture slots here: flipping maps mid-frame forces shader rebuilds and can
 * leave WebGL programs undefined (compileAsync `isReady` crash). Map changes go through
 * the outOfSync reclones path instead.
 */
function syncInstancedUvCloneLook(
  src: THREE.Material | THREE.Material[],
  dst: THREE.Material | THREE.Material[],
) {
  if (Array.isArray(src) || Array.isArray(dst)) {
    if (!Array.isArray(src) || !Array.isArray(dst)) return;
    const n = Math.min(src.length, dst.length);
    for (let i = 0; i < n; i++) syncInstancedUvCloneLookOne(src[i]!, dst[i]!);
    return;
  }
  syncInstancedUvCloneLookOne(src, dst);
}

function syncInstancedUvCloneLookOne(src: THREE.Material, dst: THREE.Material) {
  if (!(src instanceof THREE.MeshStandardMaterial) || !(dst instanceof THREE.MeshStandardMaterial)) return;

  dst.color.copy(src.color);
  dst.roughness = src.roughness;
  dst.metalness = src.metalness;
  dst.emissive.copy(src.emissive);
  dst.emissiveIntensity = src.emissiveIntensity;
  dst.opacity = src.opacity;
  dst.transparent = src.transparent;
  dst.depthWrite = src.depthWrite;
  dst.envMapIntensity = src.envMapIntensity;

  if (src instanceof THREE.MeshPhysicalMaterial && dst instanceof THREE.MeshPhysicalMaterial) {
    const prevAniso = dst.anisotropy > 0 || !!dst.anisotropyMap;
    dst.clearcoat = src.clearcoat;
    dst.clearcoatRoughness = src.clearcoatRoughness;
    dst.transmission = src.transmission;
    dst.ior = src.ior;
    dst.thickness = src.thickness;
    dst.specularIntensity = src.specularIntensity;
    if (src.specularColor && dst.specularColor) dst.specularColor.copy(src.specularColor);
    dst.sheen = src.sheen;
    dst.sheenRoughness = src.sheenRoughness;
    if (src.sheenColor && dst.sheenColor) dst.sheenColor.copy(src.sheenColor);
    dst.anisotropy = src.anisotropy;
    dst.anisotropyRotation = src.anisotropyRotation;
    // Map slot identity matters for USE_ANISOTROPYMAP — scalar sync alone left clones
    // without the define / map after anisotropy was enabled on the live graph mat.
    if (dst.anisotropyMap !== src.anisotropyMap) {
      dst.anisotropyMap = src.anisotropyMap;
      dst.needsUpdate = true;
    }
    const nextAniso = dst.anisotropy > 0 || !!dst.anisotropyMap;
    if (prevAniso !== nextAniso) dst.needsUpdate = true;
  }

  const srcFo = (src.userData as any)?._falloffUniforms as
    | { front: { value: THREE.Color }; side: { value: THREE.Color }; pow: { value: number } }
    | undefined;
  const dstFo = (dst.userData as any)?._falloffUniforms as typeof srcFo;
  if (srcFo && dstFo) {
    dstFo.front.value.copy(srcFo.front.value);
    dstFo.side.value.copy(srcFo.side.value);
    dstFo.pow.value = srcFo.pow.value;
  }

  const srcLayers = (src.userData as any)?._layerUniforms as
    | Array<{
        color: { value: THREE.Color };
        bumpScale: { value: number };
        roughness: { value: number };
        metalness: { value: number };
        amount: { value: number };
      }>
    | undefined;
  const dstLayers = (dst.userData as any)?._layerUniforms as typeof srcLayers;
  if (srcLayers && dstLayers) {
    const n = Math.min(srcLayers.length, dstLayers.length);
    for (let i = 0; i < n; i++) {
      dstLayers[i]!.color.value.copy(srcLayers[i]!.color.value);
      dstLayers[i]!.bumpScale.value = srcLayers[i]!.bumpScale.value;
      dstLayers[i]!.roughness.value = srcLayers[i]!.roughness.value;
      dstLayers[i]!.metalness.value = srcLayers[i]!.metalness.value;
      dstLayers[i]!.amount.value = srcLayers[i]!.amount.value;
    }
  }
}

function disposeMaterialOrList(mat: THREE.Material | THREE.Material[]) {
  if (Array.isArray(mat)) {
    for (const m of mat) m.dispose();
  } else {
    mat.dispose();
  }
}

function disposeInstancedMeshResources(im: THREE.InstancedMesh) {
  const u = im.userData as { __c3dDisposeGeom?: boolean; __c3dDisposeMat?: boolean };
  if (u.__c3dDisposeGeom && im.geometry) {
    im.geometry.dispose();
  }
  if (u.__c3dDisposeMat) {
    const mats = Array.isArray(im.material) ? im.material : [im.material];
    for (const m of mats) {
      if (m && typeof (m as THREE.Material).dispose === 'function') (m as THREE.Material).dispose();
    }
  }
}

export function ModifiedMesh({
  children,
  modifiers,
  visible = true,
  stayOnTop = false,
  getCenterPosition,
  instancePointerHandlers,
}: ModifiedMeshProps) {
  const groupRef = useRef<THREE.Group>(null);
  const instanceGroupRef = useRef<THREE.Group>(null);
  const uniformsRef = useRef<ModifierUniforms | null>(null);
  const compiledRef = useRef<CompiledShaderResult | null>(null);
  const prevModKeyRef = useRef('');
  const patchedMatsRef = useRef<Set<THREE.Material>>(new Set());
  const origMatsRef = useRef<Map<THREE.Mesh, THREE.Material>>(new Map());
  const instancedMeshesRef = useRef<THREE.InstancedMesh[]>([]);
  const prevArrayKeyRef = useRef('');

  const sorted = useMemo(() => {
    if (!modifiers || modifiers.length === 0) return [];
    return [...modifiers].sort((a, b) => a.order - b.order);
  }, [modifiers]);

  const gpuMods = useMemo(
    () => sorted.filter((m) => m.enabled && GPU_TYPES.has(m.type)) as GpuModifierDef[],
    [sorted],
  );

  const cpuMods = useMemo(
    () => sorted.filter((m) => m.enabled && CPU_TYPES.has(m.type)) as CpuModifierDef[],
    [sorted],
  );

  const arrayMod = useMemo(
    () => sorted.find((m) => m.enabled && ARRAY_TYPES.has(m.type)) ?? null,
    [sorted],
  );

  const modKey = useMemo(() => {
    return gpuMods.map((m) => `${m.type}:${m.enabled}`).join('|');
  }, [gpuMods]);

  const arrayKey = useMemo(() => {
    if (!arrayMod) return '';
    return `${arrayMod.type}:${JSON.stringify(arrayMod.params)}`;
  }, [arrayMod]);

  const restoreOriginalMaterials = useCallback(() => {
    if (!groupRef.current) return;
    groupRef.current.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const orig = origMatsRef.current.get(child);
      if (orig) {
        child.material = orig;
      }
    });
    patchedMatsRef.current.clear();
  }, []);

  const recompile = useCallback(() => {
    const result = compileModifierShader(gpuMods);
    compiledRef.current = result;
    uniformsRef.current = result?.uniforms ?? null;
    if (!result) restoreOriginalMaterials();
    return result;
  }, [gpuMods, restoreOriginalMaterials]);

  useEffect(() => {
    if (modKey === prevModKeyRef.current) return;
    prevModKeyRef.current = modKey;
    restoreOriginalMaterials();
    recompile();
  }, [modKey, recompile, restoreOriginalMaterials]);

  const cpuKey = useMemo(
    () => cpuMods.length > 0 ? cpuMods.map((m) => `${m.type}:${JSON.stringify(m.params)}`).join('|') : '',
    [cpuMods],
  );

  const invalidate = useThree((s) => s.invalidate);

  /** Apply CPU modifiers to every mesh under the group. Returns how many meshes were updated. */
  const applyCpuModsNow = useCallback(() => {
    if (!groupRef.current || cpuMods.length === 0) return 0;
    let n = 0;
    groupRef.current.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      if (!child.userData.__originalGeometry) {
        child.userData.__originalGeometry = child.geometry.clone();
      }
      const origGeom = child.userData.__originalGeometry as THREE.BufferGeometry;
      const modified = applyCpuModifiers(origGeom.clone(), cpuMods);
      child.geometry.dispose();
      child.geometry = modified;
      child.userData.__c3dCpuGeomActive = true;
      n++;
    });
    return n;
  }, [cpuMods]);

  /** True until CPU modifiers have been applied for the current cpuKey (meshes may mount late). */
  const cpuPendingRef = useRef(false);

  useLayoutEffect(() => {
    if (!groupRef.current) return;
    if (cpuMods.length === 0) {
      cpuPendingRef.current = false;
      groupRef.current.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        if (!child.userData.__originalGeometry) {
          child.userData.__originalGeometry = child.geometry.clone();
        }
        // Only restore when we previously replaced the geom with CPU mods.
        // Blindly re-cloning every layout pass wiped GlbModel's forced MikkT tangents.
        if (!child.userData.__c3dCpuGeomActive) return;
        const origGeom = child.userData.__originalGeometry as THREE.BufferGeometry;
        child.geometry.dispose();
        child.geometry = origGeom.clone();
        child.userData.__c3dCpuGeomActive = false;
        if (!child.geometry.getAttribute('normal')) {
          child.geometry.computeVertexNormals();
        }
        const mat = child.material;
        if (mat instanceof THREE.MeshPhysicalMaterial) {
          const anisoOn = (mat.anisotropy ?? 0) > 0 || !!mat.anisotropyMap;
          if (anisoOn || mat.normalMap) {
            ensureGeometryTangents(child.geometry, { force: true });
          }
        }
      });
      invalidate();
      return;
    }
    // Demand frameloop: applying only in useFrame can stall forever if nothing else
    // invalidates. Prefer a synchronous layout apply; keep pending if meshes are not mounted yet.
    const n = applyCpuModsNow();
    cpuPendingRef.current = n === 0;
    invalidate();
  }, [cpuKey, cpuMods.length, applyCpuModsNow, invalidate]);

  useFrame(() => {
    if (!cpuPendingRef.current || !groupRef.current || cpuMods.length === 0) return;
    const n = applyCpuModsNow();
    if (n > 0) {
      cpuPendingRef.current = false;
      invalidate();
    }
  });

  // Array modifier — build instance matrices; actual InstancedMesh creation in useFrame
  // so we always have up-to-date world matrices from the source meshes
  const arrayMatricesRef = useRef<THREE.Matrix4[] | null>(null);
  const arrayDirtyRef = useRef(false);

  useEffect(() => {
    if (!arrayMod) {
      // Leaving array mode: tear down instances and show the source mesh again.
      for (const im of instancedMeshesRef.current) {
        im.removeFromParent();
        disposeInstancedMeshResources(im);
        im.dispose();
      }
      instancedMeshesRef.current = [];
      if (groupRef.current) {
        groupRef.current.traverse((child) => {
          if (child instanceof THREE.Mesh) child.visible = true;
        });
        delete (groupRef.current.userData as { getC3dInstancedMeshes?: unknown }).getC3dInstancedMeshes;
      }
      arrayMatricesRef.current = null;
      arrayDirtyRef.current = false;
      return;
    }

    // Still in array mode (params / which Array toggled): keep old InstancedMeshes
    // until useFrame builds replacements in the same tick. Disposing here used to
    // un-hide the single source mesh for 1+ frames → flicker on straw/carpaint swaps.
    arrayMatricesRef.current = computeArrayMatrices(arrayMod);
    arrayDirtyRef.current = true;
    if (instancedMeshesRef.current.length > 0 && groupRef.current) {
      groupRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh) child.visible = false;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrayKey, arrayMod]);

  useFrame(() => {
    if (!groupRef.current) return;

    // --- Array instancing (create / update) ---
    const localMatrices = arrayMatricesRef.current;
    if (localMatrices && localMatrices.length > 0 && instanceGroupRef.current && arrayMod) {
      const sourceMeshes: THREE.Mesh[] = [];
      groupRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh) sourceMeshes.push(child);
      });

      // Hide originals so only instances render
      for (const sm of sourceMeshes) sm.visible = false;

      // Re-mark dirty if source meshes appeared after the initial dirty tick
      if (!arrayDirtyRef.current && sourceMeshes.length > 0 && instancedMeshesRef.current.length === 0) {
        arrayDirtyRef.current = true;
      }

      // Create InstancedMeshes when dirty — build first, then dispose old (same frame, no flash).
      if (arrayDirtyRef.current && sourceMeshes.length > 0) {
        arrayDirtyRef.current = false;
        const prevInstances = instancedMeshesRef.current;
        const nextInstances: THREE.InstancedMesh[] = [];

        for (const srcMesh of sourceMeshes) {
          const srcGeom = srcMesh.geometry;
          const srcMat = srcMesh.material as THREE.Material | THREE.Material[];
          if (!srcGeom || !srcMat) continue;

          const uvSetup = resolveInstanceUvSetup(arrayMod.params, srcMat);
          let geom: THREE.BufferGeometry = srcGeom;
          let mat: THREE.Material | THREE.Material[] = srcMat;
          if (uvSetup.enabled) {
            geom = srcGeom.clone();
            fillInstanceUvAttributes(geom, localMatrices.length, uvSetup.seed, uvSetup.params);
            mat = applyInstancedUvMaterial(srcMat);
            // Tangents may already be on the source; copy before first draw.
            if (srcGeom.hasAttribute('tangent')) {
              geom.setAttribute('tangent', srcGeom.getAttribute('tangent').clone());
              (geom.userData as { __c3dTanCopiedCount?: number }).__c3dTanCopiedCount =
                srcGeom.getAttribute('tangent').count;
            } else {
              ensureGeometryTangents(geom);
            }
          }

          const instMesh = new THREE.InstancedMesh(geom, mat, localMatrices.length);
          instMesh.castShadow = srcMesh.castShadow;
          instMesh.receiveShadow = srcMesh.receiveShadow;
          instMesh.frustumCulled = false;
          instMesh.userData.excludeFromRefraction = !!srcMesh.userData.excludeFromRefraction
            || /decanter\s*metal|metal\s*-\s*element/i.test(srcMesh.name)
            || /decanter\s*metal|metal\s*-\s*element/i.test(groupRef.current?.parent?.name || '');
          instMesh.onBeforeRender = transmissionFrontCullInstancedBeforeRender;
          instMesh.onAfterRender = transmissionFrontCullInstancedAfterRender;
          if (uvSetup.enabled) {
            (instMesh.userData as { __c3dDisposeGeom?: boolean; __c3dDisposeMat?: boolean }).__c3dDisposeGeom = true;
            (instMesh.userData as { __c3dDisposeGeom?: boolean; __c3dDisposeMat?: boolean }).__c3dDisposeMat = true;
            rememberInstancedUvMaterials(srcMat, instMesh.userData as InstUvTrackUserData);
          }
          instanceGroupRef.current!.add(instMesh);
          nextInstances.push(instMesh);
        }

        instancedMeshesRef.current = nextInstances;
        for (const im of prevInstances) {
          im.removeFromParent();
          disposeInstancedMeshResources(im);
          im.dispose();
        }
      }

      // Check if center-based radial array should be used
      const centerPos = getCenterPosition?.();
      const useCenterMode = centerPos && arrayMod?.type === 'arrayRadial' && arrayMod.params.centerObjectId;

      // Update instance matrices every frame using current world transforms.
      for (let mi = 0; mi < instancedMeshesRef.current.length && mi < sourceMeshes.length; mi++) {
        const instMesh = instancedMeshesRef.current[mi];
        const srcMesh = sourceMeshes[mi];

        srcMesh.updateWorldMatrix(true, false);
        srcMesh.matrixWorld.decompose(_worldPos, _worldQuat, _worldScale);

        if (useCenterMode) {
          const count = localMatrices.length;
          const axis = (arrayMod.params.axis as string) || 'y';
          const totalAngle = ((arrayMod.params.totalAngle as number) || 360) * DEG2RAD;
          const align = arrayMod.params.alignToCircle as boolean;
          const heightOffset = (arrayMod.params.offsetY as number) || 0;
          _centerVec.set(centerPos.x, centerPos.y, centerPos.z);
          _offsetVec.subVectors(_worldPos, _centerVec);

          for (let i = 0; i < count; i++) {
            const angle = (totalAngle / count) * i;
            _rotQuat.setFromAxisAngle(
              axis === 'y' ? _axisY : axis === 'x' ? _axisX : _axisZ,
              angle,
            );
            _rotVec.copy(_offsetVec).applyQuaternion(_rotQuat);
            _instancePos.addVectors(_centerVec, _rotVec);
            if (heightOffset !== 0) {
              if (axis === 'y') _instancePos.y += heightOffset * i;
              else if (axis === 'x') _instancePos.x += heightOffset * i;
              else _instancePos.z += heightOffset * i;
            }
            _instanceQuat.copy(_worldQuat);
            if (align) _instanceQuat.premultiply(_rotQuat);
            _tempMat.compose(_instancePos, _instanceQuat, _worldScale);
            instMesh.setMatrixAt(i, _tempMat);
          }
        } else {
          // Standard mode: base (pos+rot) * arrayOffset * scale
          _baseMat.compose(_worldPos, _worldQuat, _oneVec);
          _scaleMat.makeScale(_worldScale.x, _worldScale.y, _worldScale.z);

          for (let i = 0; i < localMatrices.length; i++) {
            _tempMat.multiplyMatrices(_baseMat, localMatrices[i]);
            _tempMat.multiply(_scaleMat);
            instMesh.setMatrixAt(i, _tempMat);
          }
        }
        instMesh.instanceMatrix.needsUpdate = true;

        const sm = srcMesh.material as THREE.Material | THREE.Material[] | null | undefined;
        const uvSetup = sm
          ? resolveInstanceUvSetup(arrayMod.params, sm)
          : { enabled: false, params: arrayMod.params, seed: 1 };

        if (uvSetup.enabled && sm) {
          const ud = instMesh.userData as InstUvTrackUserData & {
            __c3dDisposeGeom?: boolean;
            __c3dDisposeMat?: boolean;
          };
          const key = instanceUvParamsKey(uvSetup.seed, uvSetup.params);
          const geom = instMesh.geometry;
          const hasUvAttrs =
            !!geom.getAttribute(C3D_INST_UV_ATTR) && !!geom.getAttribute(C3D_INST_UV_SCL_ATTR);
          const transitioning = Array.isArray(sm)
            ? sm.some((m) => !!(m.userData as { __c3dMatTransitioning?: boolean })?.__c3dMatTransitioning)
            : !!(sm.userData as { __c3dMatTransitioning?: boolean })?.__c3dMatTransitioning;
          // Create attrs as soon as possible; only skip *re*-fills while the material
          // settle is running (deferred maps used to change the UV key mid-transition).
          const needsUvAttrs =
            !hasUvAttrs || (!transitioning && ud.__c3dIvParamsKey !== key);
          if (needsUvAttrs) {
            if (geom === srcMesh.geometry) {
              instMesh.geometry = srcMesh.geometry.clone();
              ud.__c3dDisposeGeom = true;
            }
            fillInstanceUvAttributes(
              instMesh.geometry,
              localMatrices.length,
              uvSetup.seed,
              uvSetup.params,
            );
            ud.__c3dIvParamsKey = key;
            syncInstanceGeometryTangents(srcMesh, instMesh);
          } else {
            syncInstanceGeometryTangents(srcMesh, instMesh);
          }

          const curMat = instMesh.material as THREE.Material | THREE.Material[];
          const curHasInstUv = Array.isArray(curMat)
            ? curMat.some((m) => !!(m as THREE.Material & { defines?: Record<string, unknown> }).defines?.C3D_INST_UV)
            : !!(curMat as THREE.Material & { defines?: Record<string, unknown> }).defines?.C3D_INST_UV;
          if (!curHasInstUv || instancedUvMaterialsOutOfSync(sm, ud)) {
            const prev = instMesh.material;
            instMesh.material = applyInstancedUvMaterial(sm);
            ud.__c3dDisposeMat = true;
            rememberInstancedUvMaterials(sm, ud);
            if (ud.__c3dDisposeMat && prev && prev !== sm) {
              disposeMaterialOrList(prev as THREE.Material | THREE.Material[]);
            }
          } else {
            // Color / soft PBR lerps do not bump `mat.version`, so outOfSync never
            // fires mid-transition — Cap would finish, then Body clones jump.
            syncInstancedUvCloneLook(sm, instMesh.material as THREE.Material | THREE.Material[]);
          }
        } else {
          // Instance-UV inactive (incl. identity-range no-op): share the live source material
          // so graph overrides (Metal Mesh tint/maps) reach InstancedMeshes.
          // Drop Straw Per-mesh UV attrs by restoring source geometry when leaving instance-UV mode.
          const ud = instMesh.userData as InstUvTrackUserData & {
            __c3dDisposeMat?: boolean;
            __c3dDisposeGeom?: boolean;
          };
          if (instMesh.geometry !== srcMesh.geometry) {
            const prevGeom = instMesh.geometry;
            instMesh.geometry = srcMesh.geometry;
            if (ud.__c3dDisposeGeom && prevGeom) {
              prevGeom.dispose();
            }
            ud.__c3dDisposeGeom = false;
            ud.__c3dIvParamsKey = undefined;
          }
          if (sm && !Array.isArray(sm)) {
            const prev = instMesh.material as THREE.Material | THREE.Material[];
            if (prev !== sm) {
              if (ud.__c3dDisposeMat && prev) {
                disposeMaterialOrList(prev);
              }
              ud.__c3dDisposeMat = false;
              ud.__c3dIvSrcRef = null;
              ud.__c3dIvSrcVer = undefined;
              ud.__c3dIvSrcVers = undefined;
              ud.__c3dIvParamsKey = undefined;
              instMesh.material = sm;
            }
          }
        }
      }

      if (groupRef.current) {
        (groupRef.current.userData as { getC3dInstancedMeshes?: () => THREE.InstancedMesh[] }).getC3dInstancedMeshes =
          () => instancedMeshesRef.current;
      }
    } else if (groupRef.current) {
      delete (groupRef.current.userData as { getC3dInstancedMeshes?: unknown }).getC3dInstancedMeshes;
    }

    // --- GPU shader patching ---
    if (compiledRef.current && uniformsRef.current) {
      const compiled = compiledRef.current;
      const uniforms = uniformsRef.current;

      updateModifierUniforms(uniforms, gpuMods, performance.now() / 1000);

      const patchMaterial = (mat: THREE.Material): THREE.Material => {
        if (patchedMatsRef.current.has(mat)) {
          const matAny = mat as any;
          if (matAny.userData?.__modUniforms) {
            const u = matAny.userData.__modUniforms as ModifierUniforms;
            for (const key of Object.keys(uniforms)) {
              if (u[key]) u[key].value = uniforms[key].value;
            }
          }
          return mat;
        }

        if (Array.isArray(mat)) return mat;

        const prefersInPlace = !!(mat.userData as any)?.c3dGraphMatApplied
          || !!(mat.userData as any)?.c3dPreferInPlaceModPatch;

        // Graph-driven materials must be patched live — cloning freezes Cap on the tint at
        // clone time while Body array instances keep lerping the real graph mat (Cap-first).
        const target = prefersInPlace ? mat : mat.clone();
        const origCompile = (target as any).onBeforeCompile?.bind(target);

        (target as any).onBeforeCompile = (shader: any) => {
          if (origCompile) origCompile(shader);
          for (const [key, val] of Object.entries(uniforms)) {
            shader.uniforms[key] = { value: val.value };
          }
          const vertChunk = compiled.vertexChunk;
          shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `#include <common>\n${vertChunk}`,
          );
          shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `#include <begin_vertex>\nmodifyVertex(transformed);`,
          );
          (target as any).userData = (target as any).userData || {};
          (target as any).userData.__modUniforms = shader.uniforms;
        };

        target.needsUpdate = true;
        patchedMatsRef.current.add(target);
        return target;
      };

      groupRef.current.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        const mat = child.material as THREE.Material;
        if (!mat) return;

        if (!origMatsRef.current.has(child)) {
          origMatsRef.current.set(child, mat);
        }

        if (!patchedMatsRef.current.has(mat)) {
          child.material = patchMaterial(mat);
        } else {
          patchMaterial(mat);
        }
      });

      for (const im of instancedMeshesRef.current) {
        const srcMat = im.material as THREE.Material;
        if (srcMat && !patchedMatsRef.current.has(srcMat)) {
          im.material = patchMaterial(srcMat);
        } else if (patchedMatsRef.current.has(srcMat)) {
          patchMaterial(srcMat);
        }
      }
    }

    // After graph / CPU paths replace `sourceMesh.material`, GPU patch clones it again — re-point InstancedMesh
    // at the **current** source material so transitions and clearcoat stacks stay frame-aligned with non-array meshes.
    // Also covers identity-range `randomizeInstanceUv` (treated as inactive above) so editor matches player.
    if (
      localMatrices &&
      localMatrices.length > 0 &&
      instanceGroupRef.current &&
      arrayMod
    ) {
      const sourceMeshesEnd: THREE.Mesh[] = [];
      groupRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh) sourceMeshesEnd.push(child);
      });
      for (let mi = 0; mi < instancedMeshesRef.current.length && mi < sourceMeshesEnd.length; mi++) {
        const instMesh = instancedMeshesRef.current[mi];
        const srcMesh = sourceMeshesEnd[mi];
        const sm = srcMesh.material as THREE.Material | THREE.Material[] | null | undefined;
        if (!sm || Array.isArray(sm)) continue;
        const uvSetupEnd = resolveInstanceUvSetup(arrayMod.params, sm);
        if (uvSetupEnd.enabled) continue;
        if (instMesh.material !== sm) {
          const ud = instMesh.userData as InstUvTrackUserData & { __c3dDisposeMat?: boolean };
          const prev = instMesh.material as THREE.Material | THREE.Material[];
          if (ud.__c3dDisposeMat && prev && prev !== sm) {
            disposeMaterialOrList(prev);
          }
          ud.__c3dDisposeMat = false;
          instMesh.material = sm;
        }
      }
    }
  });

  useEffect(() => {
    if (instanceGroupRef.current) instanceGroupRef.current.renderOrder = stayOnTop ? 20000 : 0;
    for (const instMesh of instancedMeshesRef.current) {
      instMesh.renderOrder = stayOnTop ? 20000 : 0;
      // Omit front-of-liquid instances from the transmission refraction buffer.
      instMesh.onBeforeRender = transmissionFrontCullInstancedBeforeRender;
      instMesh.onAfterRender = transmissionFrontCullInstancedAfterRender;
      const mats = Array.isArray(instMesh.material) ? instMesh.material : [instMesh.material];
      for (const mat of mats) {
        if (!mat) continue;
        mat.depthTest = true;
        mat.needsUpdate = true;
      }
    }
  }, [stayOnTop]);

  return (
    <>
      {/* Gate both source + array instances: GlbModel.visible alone leaves InstancedMeshes live. */}
      <group ref={groupRef} visible={visible}>{children}</group>
      <group
        ref={instanceGroupRef}
        visible={visible}
        onClick={instancePointerHandlers?.onClick}
        onPointerOver={instancePointerHandlers?.onPointerOver}
        onPointerOut={instancePointerHandlers?.onPointerOut}
      />
    </>
  );
}

const _tempMat = new THREE.Matrix4();
const _baseMat = new THREE.Matrix4();
const _scaleMat = new THREE.Matrix4();
const _worldPos = new THREE.Vector3();
const _worldQuat = new THREE.Quaternion();
const _worldScale = new THREE.Vector3();
const _oneVec = new THREE.Vector3(1, 1, 1);
const _centerVec = new THREE.Vector3();
const _offsetVec = new THREE.Vector3();
const _rotQuat = new THREE.Quaternion();
const _rotVec = new THREE.Vector3();
const _instancePos = new THREE.Vector3();
const _instanceQuat = new THREE.Quaternion();
const _axisX = new THREE.Vector3(1, 0, 0);
const _axisY = new THREE.Vector3(0, 1, 0);
const _axisZ = new THREE.Vector3(0, 0, 1);
